import cn from "@utils/cn";
import { contentfulImageLoader } from "@utils/imageLoader";
import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FC, useState } from "react";

import Image from "@/components/ui/Image/Image";
import { testimonialsCardClick } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { TileCarouselCardProps } from "@/types";

import RichText from "../RichText/RichText";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import ButtonOrVideoModal from "../ui/ButtonOrVideoModal/ButtonOrVideoModal";
import Icon from "../ui/Icon/Icon";
import Modal from "../ui/Modal/Modal";
import ModalContent from "../ui/Modal/ModalContent";
import TileCarouselCardContent from "./TileCarouselCardContent";

const TileCarouselCard: FC<{ item: TileCarouselCardProps; openModal: () => void; showInCardMode?: boolean; title?: string }> = ({
  item,
  openModal,
  showInCardMode = false,
  title,
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [modalOpen, setModalOpen] = useState(false);
  const pathname = usePathname();
  const isHomePage = pathname === "/" || pathname === `/${locale}/`;

  const sessionStorageForDatalayer = {
    clicked: true,
    section: { title: title },
  };

  const handleCardClick = () => {
    setModalOpen(true);
    testimonialsCardClick({ tile_carousel_name: title ?? "", tile_card_name: item.title, tile_card_location: isHomePage ? "Home Page" : "" });
    sessionStorage.setItem("tileCarousel", JSON.stringify(sessionStorageForDatalayer));
  };
  const CardContent = (item: TileCarouselCardProps, disableClick: boolean = false) => (
    <article className={cn(`group flex flex-col relative z-0 rounded-lg cursor-pointer`)} onClick={disableClick ? undefined : handleCardClick}>
      <div className="relative" style={{ height: isDesktop ? undefined : "calc(100% - 50px)" }}>
        {item.image && (
          <div className="relative rounded-lg overflow-hidden">
            <img
              key={`image-0`}
              alt={item.image.title || ""}
              className="aspect-4-3 md:aspect-9-16 transition transform hover:duration-[6000ms] md:group-hover:scale-105 motion-reduce:transition-none motion-reduce:hover:transform-none w-full h-full object-cover object-[0_15%] md:object-center"
              src={contentfulImageLoader({ src: item.image.url, width: 255, quality: 80 })}
              srcSet={[255, 310, 510, 620].map((w) => `${contentfulImageLoader({ src: item.image!.url, width: w, quality: 80 })} ${w}w`).join(", ")}
              sizes="(min-width: 1024px) 255px, 67vw"
              loading="lazy"
              decoding="async"
            />
            {item.logo && <div className="absolute w-full h-full top-0 left-0 inset-0 rounded-lg img-overlay-5"></div>}
            <div className="absolute w-full h-full top-0 left-0 inset-0 img-overlay-2"></div>
          </div>
        )}
      </div>
      {item.logo && (
        <div className="absolute bottom-0 top-0 text-white pt-xs flex justify-center w-1/2 mx-auto left-0 right-0">
          <Image src={item.logo.url} width={item.logo.width || 128} height={item.logo.height || 72} className="h-fit" alt={item.logo.title || ""} />
        </div>
      )}
      <div className="absolute bottom-0 z-10 text-white px-s pb-m w-full text-left md:rtl:text-right tile-carousel-content">
        {item?.overtitle && <div className="font-secondary font-normal text-xs pb-xs">{item.overtitle}</div>}
        <div className="font-secondary text-m font-medium leading-5 pb-xxs tile-carousel-title">{item.title}</div>
        {item.abstract && <div className="font-secondary text-s leading-4 tile-carousel-description">{item.abstract}</div>}
        {item?.abstractRtf && !item?.abstract && (
          <div className="font-secondary text-s leading-4 tile-carousel-description">
            <RichText content={item.abstractRtf} />
          </div>
        )}
        {item.primaryCta && (
          <div className="items-center hidden font-medium md:group-hover:flex mt-s">
            <Button
              // href={projectResultPage ? finalUrl : finalUrlForCommunity}s
              size={ButtonSize.SMALL}
              theme={ButtonTheme.WHITE}
              type="button"
              variant={ButtonVariant.PRIMARY}
              customClass="text-center !border-white hover:!border-0"
              onClick={handleCardClick}
            >
              {typeof item?.primaryCta === "object" && "buttonLabel" in item.primaryCta ? item.primaryCta.buttonLabel : ""}
            </Button>
          </div>
        )}
      </div>
    </article>
  );
  if (showInCardMode)
    return (
      <>
        {item.link ? (
          <Link href={getLink(item.link, locale)} target={item.link?.link?.target}>
            {CardContent(item, true)}
          </Link>
        ) : (
          <>{CardContent(item)}</>
        )}
        {
          <Modal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
            }}
          >
            <ModalContent className="h-dvh w-dvw !rounded-none pt-0 md:pt-m !px-0 md:!px-l">
              <div className={cn("flex items-center justify-between mb-l px-m md:px-0 pt-l")}>
                <p className={cn("font-primary uppercase")}>{item.title}</p>
                <Button
                  className={"stroke-black"}
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.TERTIARY}
                  theme={ButtonTheme.BLACK}
                  onClick={() => {
                    setModalOpen(false);
                  }}
                >
                  <span className="pointer-events-none">
                    <Icon name="close" size="24" title={t("label.common.close")} />
                    <span className="sr-only">{t("button.label.closeModal")}</span>
                  </span>
                </Button>
              </div>
              {
                <iframe
                  className={cn("object-cover w-full h-[calc(100dvh-72px)] md:rounded-lg border-0")}
                  width="560"
                  height="315"
                  src={
                    (typeof item?.primaryCta === "object" && "embeddedVideo" in item.primaryCta && item?.primaryCta?.embeddedVideo?.link?.url) || ""
                  }
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                ></iframe>
              }
            </ModalContent>
          </Modal>
        }
      </>
    );

  return (
    <>
      {item.link ? (
        <Link href={getLink(item.link, locale)} target={item.link?.link?.target}>
          <>
            <div className="w-full md:w-fit relative mb-m md:mb-m" onClick={handleCardClick}>
              <div className="w-full md:w-fit mb-m ">
                {item.image && (
                  <div className={`relative w-full overflow-hidden rounded-lg`}>
                    <img
                      src={contentfulImageLoader({ src: item.image.url, width: 310, quality: 80 })}
                      srcSet={[255, 310, 510, 620]
                        .map((w) => `${contentfulImageLoader({ src: item.image!.url, width: w, quality: 80 })} ${w}w`)
                        .join(", ")}
                      sizes="(min-width: 1024px) 285px, 100vw"
                      className="w-full md:w-[255px] h-[453px] rounded-lg shadow-elevation object-cover"
                      alt={item.image.title || ""}
                      loading="lazy"
                      decoding="async"
                    />
                    {item.logo && <div className="absolute w-full h-full top-0 left-0 inset-0 rounded-lg img-overlay-5"></div>}
                  </div>
                )}
                {item.logo && (
                  <div className="absolute bottom-0 top-0 text-white pt-xs flex justify-center w-1/2 mx-auto left-0 right-0">
                    <Image
                      src={item.logo.url}
                      width={item.logo.width || 128}
                      height={item.logo.height || 72}
                      className="h-fit"
                      alt={item.logo.title || ""}
                    />
                  </div>
                )}
                <div className="absolute bottom-0 z-10 text-white px-s pb-m w-full tile-carousel-content">
                  {item?.overtitle && <div className="font-secondary font-normal text-xs pb-xs">{item.overtitle}</div>}
                  <div className="font-secondary text-m font-medium leading-5 pb-xxs tile-carousel-title">{item.title}</div>
                  {item?.abstract && <div className="font-secondary text-s leading-4 tile-carousel-description">{item.abstract}</div>}
                  {item?.abstractRtf && !item?.abstract && (
                    <div className="font-secondary text-s leading-4 tile-carousel-description">
                      <RichText content={item.abstractRtf} />
                    </div>
                  )}
                </div>
                {/* <div className={`absolute top-0 left-0 opacity-0 bg-gold w-full h-full ${!item?.primaryCta && "pointer-events-none"}`}> */}
                <div className={`absolute top-0 left-0 w-full h-full z-50 ${!item?.primaryCta && "pointer-events-none"}`}>
                  {item?.primaryCta && (
                    <div className={`absolute top-0 left-0 w-full h-full z-50`}>
                      <ButtonOrVideoModal
                        cta={item?.primaryCta || " "}
                        modalTitle=""
                        btnClasses="hidden w-full h-full"
                        style={{ width: "100%", height: "100%", opacity: 0 }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        </Link>
      ) : item.modal ? (
        <button onClick={openModal} className="block w-full">
          <TileCarouselCardContent item={item} openModal={openModal} showInCardMode={showInCardMode} />
        </button>
      ) : (
        <TileCarouselCardContent item={item} openModal={openModal} showInCardMode={showInCardMode} />
      )}
    </>
  );
};
export default TileCarouselCard;
